import {useEffect, useMemo, useRef, useState} from 'react';
import {CancelToken} from 'axios';
import MessagesService from '../services/MessagesService';

const DEFAULT_MAX_TEXT_BYTES = 12000;
const empty = () => ({
    checks: [],
    available: false,
    maxTextBytes: DEFAULT_MAX_TEXT_BYTES,
});

const utf8ByteLength = value => {
    let bytes = 0;
    for (let index = 0; index < value.length; index += 1) {
        const code = value.charCodeAt(index);
        if (code < 0x80) bytes += 1;
        else if (code < 0x800) bytes += 2;
        else if (code >= 0xD800 && code <= 0xDBFF && index + 1 < value.length
            && value.charCodeAt(index + 1) >= 0xDC00 && value.charCodeAt(index + 1) <= 0xDFFF) {
            bytes += 4;
            index += 1;
        } else bytes += 3;
    }
    return bytes;
};

// Grammar checks are explicit, direct requests. Local state keeps the review UI
// without persisting or polling server-side work.
export default function useInboxAiGrammar({organizationId, userId, sessionKey}) {
    const identity = JSON.stringify([organizationId, userId, sessionKey]);
    const identityRef = useRef(identity);
    identityRef.current = identity;
    const runtime = useRef(null);
    const [state, setState] = useState(() => ({...empty(), identity}));
    const visible = state.identity === identity ? state : {...empty(), identity};

    const live = run => run && !run.disposed
        && identityRef.current === run.identity && runtime.current === run;
    const update = (run, change) => {
        if (live(run)) setState(previous => ({...previous, ...change(previous), identity: run.identity}));
    };

    useEffect(() => {
        const run = {identity, sequence: 0, requests: new Map(), disposed: false};
        runtime.current = run;
        setState({...empty(), identity});

        if (!organizationId || !userId || !sessionKey) {
            return () => { run.disposed = true; };
        }

        const availabilitySource = CancelToken.source();
        MessagesService.getInboxAiAvailability(organizationId, availabilitySource.token)
            .then(result => update(run, () => ({
                available: result?.available === true,
                maxTextBytes: Number(result?.maxTextBytes) > 0
                    ? Number(result.maxTextBytes) : DEFAULT_MAX_TEXT_BYTES,
            })))
            .catch(() => {});

        return () => {
            run.disposed = true;
            availabilitySource.cancel('Inbox session changed');
            run.requests.forEach(source => source.cancel('Inbox session changed'));
            run.requests.clear();
        };
        // Identity contains every authentication boundary and deliberately resets local reviews.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [identity]);

    const checkGrammar = async ({messageFeedId, text}) => {
        const run = runtime.current;
        const feedId = String(messageFeedId);
        if (!live(run) || !visible.available || run.requests.has(feedId)) return null;
        if (typeof text !== 'string' || !text.trim() || utf8ByteLength(text) > visible.maxTextBytes) {
            update(run, previous => ({checks: [{
                id: `validation:${feedId}`,
                messageFeedId: feedId,
                originalText: text,
                status: 'error',
                error: {message: `Enter a draft of no more than ${visible.maxTextBytes.toLocaleString()} bytes to check.`},
            }, ...previous.checks.filter(check => String(check.messageFeedId) !== feedId)]}));
            return null;
        }
        const checkId = `request:${feedId}:${++run.sequence}`;
        const source = CancelToken.source();
        const localCheck = {id: checkId, messageFeedId: feedId, originalText: text, status: 'in_progress'};
        run.requests.set(feedId, source);
        update(run, previous => ({
            checks: [localCheck, ...previous.checks.filter(check => String(check.messageFeedId) !== feedId)],
        }));

        try {
            const result = await MessagesService.checkInboxAiGrammar(organizationId, {
                messageFeedId: feedId,
                text,
            }, source.token);
            if (!live(run) || run.requests.get(feedId) !== source) return null;
            if (typeof result?.text !== 'string') throw new Error('Invalid grammar review response');
            const completed = {...localCheck, status: 'complete', result: {text: result.text}};
            update(run, previous => ({
                checks: [completed, ...previous.checks.filter(check => String(check.messageFeedId) !== feedId)],
            }));
            return completed;
        } catch (failure) {
            if (!live(run) || run.requests.get(feedId) !== source || source.token.reason) return null;
            update(run, previous => ({checks: [{...localCheck, status: 'error', error: {
                message: failure?.response?.data?.error?.message || 'Unable to complete the grammar check. Please try again.',
            }}, ...previous.checks.filter(check => String(check.messageFeedId) !== feedId)]}));
            return null;
        } finally {
            if (run.requests.get(feedId) === source) run.requests.delete(feedId);
        }
    };

    const clearCheck = feedId => {
        const run = runtime.current;
        if (!live(run)) return false;
        const key = String(feedId);
        const source = run.requests.get(key);
        if (source) {
            source.cancel('Grammar review dismissed');
            run.requests.delete(key);
        }
        update(run, previous => ({
            checks: previous.checks.filter(check => String(check.messageFeedId) !== key),
        }));
        return true;
    };

    const actions = useRef(null);
    actions.current = {checkGrammar, clearCheck};
    const callbacks = useMemo(() => ({
        check: input => actions.current.checkGrammar(input),
        clear: feedId => actions.current.clearCheck(feedId),
    }), []);

    const checksByFeedId = {};
    visible.checks.forEach(check => {
        const feedId = String(check.messageFeedId);
        if (!checksByFeedId[feedId]) checksByFeedId[feedId] = check;
    });
    return {...visible, checksByFeedId, ...callbacks};
}
